// Configuración del Cazador de Ofertas — por cuenta (owner).
//
// Permite DEFINIR las reglas y la FRECUENCIA con que el cazador busca ofertas:
//   - Activar/pausar el cazador.
//   - Frecuencia (cada cuántas horas corre la búsqueda programada).
//   - % de descuento mínimo bajo el precio de mercado.
//   - Nº de condiciones de vinilo top aceptadas.
//   - Precio tope absoluto opcional.
//   - Email de aviso y si avisar aunque no haya ofertas.
// Además muestra el HISTORIAL de corridas (para ver qué ha pasado) y permite
// lanzar una búsqueda ahora. Consume `GET/PUT /config/deal-hunter` y
// `GET /deal-hunter/runs`.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../api/ApiContext.js';
import { useAuth } from '../auth/AuthContext.js';
import { AlertMessage, AsyncBoundary, Badge, ButtonSpinner, Card, Icon, PageHeader } from '../ui/components.js';
import { useAsync } from '../ui/useAsync.js';
import type { DealAlert, DealHunterConfig, DealHunterRun } from '../types.js';

/** Opciones de frecuencia (se guardan como horas); la etiqueta se resuelve vía i18n. */
const FREQUENCY_OPTIONS: { hours: number; labelKey: string }[] = [
  { hours: 12, labelKey: 'dealHunter.freq.h12' },
  { hours: 24, labelKey: 'dealHunter.freq.daily' },
  { hours: 72, labelKey: 'dealHunter.freq.d3' },
  { hours: 168, labelKey: 'dealHunter.freq.weekly' },
  { hours: 720, labelKey: 'dealHunter.freq.monthly' },
];

/** Fecha/hora legible en el locale del navegador, o guion. */
function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

export function DealHunterSettingsView() {
  const { t } = useTranslation();
  const api = useApi();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('deal_hunter', 'read_write');
  const RUNS_PAGE_SIZE = 10;
  const [runsOffset, setRunsOffset] = useState(0);
  const config = useAsync(() => api.dealHunter.getConfig(), []);
  // Historial paginado: máximo 10 por página.
  const runs = useAsync(() => api.dealHunter.runs({ limit: RUNS_PAGE_SIZE, offset: runsOffset }), [runsOffset]);
  const alerts = useAsync(() => api.dealHunter.alerts(), []);

  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  async function runNow() {
    setRunning(true);
    setError(null);
    setMessage(null);
    try {
      const r = await api.dealHunter.run();
      setMessage(t('dealHunter.runResult', { count: r.deals_found }));
      // Vuelve a la primera página para ver la corrida recién hecha.
      if (runsOffset === 0) runs.reload();
      else setRunsOffset(0);
      alerts.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('dealHunter.errRun'));
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="flex flex-col">
      <PageHeader
        title={t('dealHunter.pageTitle')}
        subtitle={t('dealHunter.pageSubtitle')}
        back
      />

      <div className="flex flex-col px-margin-mobile py-space-md gap-space-md">
        <AlertMessage message={error} />
        {message && (
          <div className="p-space-sm rounded-lg bg-goldmine-mint/15 border border-goldmine-mint/40 text-goldmine-mint font-body-sm text-body-sm">
            {message}
          </div>
        )}

        <AsyncBoundary state={config}>
          {(cfg) => (
            <ConfigForm
              initial={cfg}
              canEdit={canEdit}
              onError={setError}
              onSaved={() => {
                setMessage(t('dealHunter.msgSaved'));
                config.reload();
              }}
            />
          )}
        </AsyncBoundary>

        {canEdit && (
          <button className="btn-secondary self-start" type="button" onClick={() => void runNow()} disabled={running}>
            {running ? <ButtonSpinner /> : <Icon name="travel_explore" className="text-[18px]" />}
            {t('dealHunter.searchNow')}
          </button>
        )}

        {/* Ofertas detectadas por el cazador. */}
        <Card title={t('dealHunter.dealsTitle')} icon="sell">
          <AsyncBoundary state={alerts}>
            {(data) => <AlertsList alerts={data.items} />}
          </AsyncBoundary>
        </Card>

        {/* Historial de corridas (paginado, máx 10 por página). */}
        <Card title={t('dealHunter.runsTitle')} icon="history">
          <AsyncBoundary state={runs}>
            {(data) => (
              <RunsTable
                runs={data.items}
                page={Math.floor(runsOffset / RUNS_PAGE_SIZE) + 1}
                hasPrev={runsOffset > 0}
                hasNext={data.items.length === RUNS_PAGE_SIZE}
                onPrev={() => setRunsOffset((o) => Math.max(0, o - RUNS_PAGE_SIZE))}
                onNext={() => setRunsOffset((o) => o + RUNS_PAGE_SIZE)}
              />
            )}
          </AsyncBoundary>
        </Card>
      </div>
    </div>
  );
}

/** Lista de ofertas detectadas (artista/título/vendedor/precio/descuento/link). */
function AlertsList({ alerts }: { alerts: DealAlert[] }) {
  const { t } = useTranslation();
  if (alerts.length === 0) {
    return <p className="font-body-sm text-body-sm text-on-surface-variant">{t('dealHunter.noDeals')}</p>;
  }
  return (
    <ul className="flex flex-col gap-space-sm">
      {alerts.map((a) => (
        <li key={a.id} className="surface-rack-card !p-space-sm flex items-center justify-between gap-space-sm">
          <div className="min-w-0">
            <div className="font-body-md text-body-md text-on-surface truncate">
              {a.artist ?? '—'} — {a.title ?? '—'}
            </div>
            <div className="flex items-center gap-space-xs font-body-sm text-body-sm text-on-surface-variant truncate">
              {a.seller ?? ''} · {a.media_condition ?? ''} ·{' '}
              <span className="font-code-catalog text-on-surface">
                {a.price ?? '—'} {a.currency ?? ''}
              </span>
              {a.discount_pct != null && <Badge tone="success">-{a.discount_pct.toFixed(0)}%</Badge>}
            </div>
          </div>
          {a.marketplace_url && (
            <a
              className="w-9 h-9 shrink-0 rounded bg-primary-container/20 text-primary flex items-center justify-center"
              href={a.marketplace_url}
              target="_blank"
              rel="noreferrer"
              aria-label={t('dealHunter.viewDeal')}
            >
              <Icon name="open_in_new" className="text-[18px]" />
            </a>
          )}
        </li>
      ))}
    </ul>
  );
}

/** Formulario editable de las reglas y la frecuencia. */
function ConfigForm({
  initial,
  canEdit,
  onError,
  onSaved,
}: {
  initial: DealHunterConfig;
  canEdit: boolean;
  onError: (m: string | null) => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const [enabled, setEnabled] = useState(initial.enabled);
  const [frequency, setFrequency] = useState(initial.frequency_hours);
  const [discount, setDiscount] = useState(String(initial.discount_threshold_pct));
  const [topConditions, setTopConditions] = useState(initial.top_conditions);
  const [maxPrice, setMaxPrice] = useState(initial.max_price === null ? '' : String(initial.max_price));
  const [notifyEmail, setNotifyEmail] = useState(initial.notify_email ?? '');
  const [sendEmpty, setSendEmpty] = useState(initial.send_empty);
  const [busy, setBusy] = useState(false);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    onError(null);
    try {
      await api.dealHunter.updateConfig({
        enabled,
        frequency_hours: frequency,
        discount_threshold_pct: Number(discount),
        top_conditions: topConditions,
        max_price: maxPrice.trim() === '' ? null : Number(maxPrice),
        notify_email: notifyEmail.trim() === '' ? null : notifyEmail.trim(),
        send_empty: sendEmpty,
      });
      onSaved();
    } catch (err) {
      onError(err instanceof Error ? err.message : t('dealHunter.errSaveConfig'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={t('dealHunter.rulesTitle')} icon="tune">
      <form onSubmit={save} className="flex flex-col gap-space-sm">
        <label className="flex items-center justify-between gap-space-sm surface-rack-card !p-space-sm">
          <span className="flex items-center gap-space-xs font-body-md text-body-md text-on-surface">
            <Icon name="power_settings_new" className="text-primary text-[18px]" /> {t('dealHunter.activeHunter')}
          </span>
          <input type="checkbox" checked={enabled} disabled={!canEdit} onChange={(e) => setEnabled(e.target.checked)} />
        </label>

        <label className="field-label">{t('dealHunter.frequencyLabel')}</label>
        <select
          className="field-input"
          value={frequency}
          disabled={!canEdit}
          onChange={(e) => setFrequency(Number(e.target.value))}
        >
          {FREQUENCY_OPTIONS.map((o) => (
            <option key={o.hours} value={o.hours}>
              {t(o.labelKey)}
            </option>
          ))}
          {/* Si el valor guardado no está en la lista, lo mostramos igual. */}
          {!FREQUENCY_OPTIONS.some((o) => o.hours === frequency) && (
            <option value={frequency}>{t('dealHunter.freq.customHours', { hours: frequency })}</option>
          )}
        </select>

        <label className="field-label">{t('dealHunter.minDiscount')}</label>
        <input
          className="field-input"
          type="number"
          min={0}
          max={99}
          value={discount}
          disabled={!canEdit}
          onChange={(e) => setDiscount(e.target.value)}
        />
        <span className="font-body-sm text-body-sm text-on-surface-variant">
          {t('dealHunter.minDiscountHint')}
        </span>

        <label className="field-label">{t('dealHunter.conditionsAccepted')}</label>
        <select
          className="field-input"
          value={topConditions}
          disabled={!canEdit}
          onChange={(e) => setTopConditions(Number(e.target.value))}
        >
          {[1, 2, 3, 4, 5, 6, 7].map((n) => (
            <option key={n} value={n}>
              {t(`dealHunter.condition.${n}`)}
            </option>
          ))}
        </select>

        <label className="field-label">{t('dealHunter.maxPrice')}</label>
        <input
          className="field-input"
          type="number"
          min={0}
          step="0.01"
          placeholder={t('dealHunter.maxPricePlaceholder')}
          value={maxPrice}
          disabled={!canEdit}
          onChange={(e) => setMaxPrice(e.target.value)}
        />
        <span className="font-body-sm text-body-sm text-on-surface-variant">
          {t('dealHunter.maxPriceHint')}
        </span>

        <label className="field-label">{t('dealHunter.notifyEmail')}</label>
        <input
          className="field-input"
          type="email"
          placeholder={t('dealHunter.notifyEmailPlaceholder')}
          value={notifyEmail}
          disabled={!canEdit}
          onChange={(e) => setNotifyEmail(e.target.value)}
        />

        <label className="flex items-center gap-space-xs font-body-sm text-body-sm text-on-surface">
          <input type="checkbox" checked={sendEmpty} disabled={!canEdit} onChange={(e) => setSendEmpty(e.target.checked)} />
          {t('dealHunter.sendEmpty')}
        </label>

        <p className="font-body-sm text-body-sm text-on-surface-variant">
          {t('dealHunter.lastRun', { date: fmtDate(initial.last_run_at) })}
        </p>

        {canEdit && (
          <button className="btn-primary btn-sm self-start" type="submit" disabled={busy}>
            {busy ? t('dealHunter.saving') : t('dealHunter.saveConfig')}
          </button>
        )}
      </form>
    </Card>
  );
}

/** Tabla del historial de corridas, paginada (máx 10 por página). */
function RunsTable({
  runs,
  page,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
}: {
  runs: DealHunterRun[];
  page: number;
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
}) {
  const { t } = useTranslation();
  if (runs.length === 0 && !hasPrev) {
    return <p className="font-body-sm text-body-sm text-on-surface-variant">{t('dealHunter.noRuns')}</p>;
  }
  return (
    <div className="flex flex-col gap-space-xs">
      {runs.map((r) => (
        <div key={r.id} className="surface-rack-card !p-space-sm flex items-center justify-between gap-space-sm">
          <span className="flex flex-col min-w-0">
            <span className="font-body-sm text-body-sm text-on-surface">
              {fmtDate(r.created_at)} · {r.trigger === 'manual' ? t('dealHunter.triggerManual') : t('dealHunter.triggerScheduled')}
            </span>
            <span className="font-code-catalog text-body-sm text-on-surface-variant">
              {t('dealHunter.dealsOfEvaluated', { deals: r.deals_found, evaluated: r.items_evaluated })}
              {r.emails_sent > 0 ? t('dealHunter.emailsSuffix', { count: r.emails_sent }) : ''}
              {r.error ? t('dealHunter.errorSuffix', { error: r.error }) : ''}
            </span>
          </span>
          <Badge tone={r.status === 'completed' ? 'success' : r.status === 'error' ? 'danger' : 'info'}>
            {r.status === 'completed' ? t('dealHunter.runCompleted') : r.status === 'error' ? t('dealHunter.runError') : t('dealHunter.runInProgress')}
          </Badge>
        </div>
      ))}

      {(hasPrev || hasNext) && (
        <div className="flex items-center justify-between gap-space-sm pt-space-xs">
          <button className="btn-ghost btn-sm" type="button" onClick={onPrev} disabled={!hasPrev}>
            <Icon name="chevron_left" className="text-[18px]" /> {t('dealHunter.prev')}
          </button>
          <span className="font-code-catalog text-body-sm text-on-surface-variant">{t('dealHunter.page', { page })}</span>
          <button className="btn-ghost btn-sm" type="button" onClick={onNext} disabled={!hasNext}>
            {t('dealHunter.nextRuns')} <Icon name="chevron_right" className="text-[18px]" />
          </button>
        </div>
      )}
    </div>
  );
}
